// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { RefObject, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'copilotChatScroll';
const BOTTOM_THRESHOLD = 32;
const CONTENT_SETTLE_TIMEOUT = 300;

type SavedScroll = {
    chatId: string;
    scrollTop: number;
    atBottom: boolean;
};

const getPosition = (listEl: HTMLDivElement, chatId: string): SavedScroll => ({
    chatId,
    scrollTop: listEl.scrollTop,
    atBottom: listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight <= BOTTOM_THRESHOLD,
});

const readSavedScroll = (): SavedScroll | null => {
    try {
        const saved = window.sessionStorage.getItem(STORAGE_KEY);
        if (saved === null) {
            return null;
        }

        const parsed = JSON.parse(saved) as Partial<SavedScroll> | null;
        if (
            typeof parsed?.chatId !== 'string'
            || typeof parsed.scrollTop !== 'number'
            || !Number.isFinite(parsed.scrollTop)
            || typeof parsed.atBottom !== 'boolean'
        ) {
            return null;
        }

        return parsed as SavedScroll;
    } catch {
        return null;
    }
};

const writeSavedScroll = (value: SavedScroll) => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {}
};

type UseChatScrollRestorationProps = {
    listRef: RefObject<HTMLDivElement>;
    chatId: string | undefined;
};

export const useChatScrollRestoration = ({ listRef, chatId }: UseChatScrollRestorationProps) => {
    const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
    const savedScrollRef = useRef<SavedScroll | null>(null);
    const chatIdRef = useRef(chatId);
    chatIdRef.current = chatId;

    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl || !contentEl) {
            return;
        }

        const remember = () => {
            const currentChatId = chatIdRef.current;
            if (currentChatId === undefined) {
                return;
            }
            savedScrollRef.current = getPosition(listEl, currentChatId);
        };

        const save = () => {
            if (savedScrollRef.current) {
                writeSavedScroll(savedScrollRef.current);
            }
        };

        listEl.addEventListener('scroll', remember, { passive: true });
        window.addEventListener('pagehide', save);

        return () => {
            listEl.removeEventListener('scroll', remember);
            window.removeEventListener('pagehide', save);
            save();
        };
    }, [listRef, contentEl]);

    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl || !contentEl || chatId === undefined) {
            return;
        }

        const saved = readSavedScroll();
        const target = saved?.chatId === chatId && !saved.atBottom ? saved.scrollTop : null;

        let settleTimeoutId: ReturnType<typeof setTimeout>;
        const observer = new ResizeObserver(() => {
            listEl.scrollTop = target ?? listEl.scrollHeight;
            savedScrollRef.current = getPosition(listEl, chatId);
            clearTimeout(settleTimeoutId);
            settleTimeoutId = setTimeout(() => observer.disconnect(), CONTENT_SETTLE_TIMEOUT);
        });
        observer.observe(contentEl);

        return () => {
            clearTimeout(settleTimeoutId);
            observer.disconnect();
        };
    }, [listRef, contentEl, chatId]);

    return setContentEl;
};

export default useChatScrollRestoration;
